'use client'
import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { useRouter } from "next/navigation";
import {
  Box,
  Flex,
  Image,
  Text,
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Icon,
  Link,
  useToast,
  Switch,
  FormLabel, 
  InputGroup,
  InputRightElement,
  Input
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { useAppContext } from "hooks";
import { useDeleteModel, useUpdateCheckpointAutoExtend, useUpdateModelDisplayName, useUpdateModelThumbnail } from "queries"; 
import { Model } from "types";
import { POST_EXTEND_CKPT, POST_EXTEND_FLUX } from "api";
import { fetch } from "libs";

const ModelCard = ({
  model,
  refetch,
  navigateToMyModels = false,
}: {
  model: Model;
  refetch: () => void;
  navigateToMyModels?: boolean;
}) => {
  const { profile } = useAppContext();
  const router = useRouter();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isExtendOpen,
    onOpen: onExtendOpen,
    onClose: onExtendClose,
  } = useDisclosure();
  const {
    isOpen: isConfigOpen,
    onOpen: onConfigOpen,
    onClose: onConfigClose,
  } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expireInMilliSecs, setExpireInMilliSecs] = useState<number>(0);
  const [autoExtend, setAutoExtend] = useState<boolean>(
    model.isFlux 
      ? model.auto_extend || false
      : (model.checkpoints?.length && model.checkpoints[0].auto_extend) || false
  );
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
const [modelDisplayName, setModelDisplayName] = useState<string>(model.display_name || "");
const fileInputRef = useRef<HTMLInputElement>(null);
const [isUpdatingThumbnail, setIsUpdatingThumbnail] = useState<boolean>(false);
const updateModelThumbnail = useUpdateModelThumbnail();

const nameInputRef = useRef<HTMLInputElement>(null);
const updateModelDisplayName = useUpdateModelDisplayName();
    const deleteModel = useDeleteModel();
  const updateAutoExtend = useUpdateCheckpointAutoExtend();
  const isUserModels = navigateToMyModels || model.user_id != null; 
  const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;

  const updatedAt = model.isFlux
    ? new Date(model.updated_at).getTime()
    : null;

  const fluxExpireInMilliSecs = updatedAt
    ? updatedAt + THIRTY_DAYS_IN_MS - new Date().getTime()
    : 0;

  useEffect(() => {
    if (
      isUserModels &&
      model.checkpoints?.length &&
      model.checkpoints[0].expire_at !== null
    ) {
      setExpireInMilliSecs(
        new Date(model.checkpoints[0].expire_at).getTime() -
          new Date().getTime()
      );
    }
  }, [isUserModels, model.checkpoints]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleDelete = () => {
    deleteModel.mutate(
      { model_id: model.id, model_username: model.username },
      {
        onSuccess: () => {
          onClose();
          refetch();
        },
        onError: (error) => {
          console.error("Error deleting model:", error);
          toast({
            title: "Error",
            description: "Unable to delete model. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
      }
    );
  };


  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  
const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file) return;
  
  // Validate file is an image
  if (!file.type.startsWith('image/')) {
    toast({
      title: "Invalid file type",
      description: "Please select an image file.",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
    return;
  }
  
  // Validate file size (e.g. 5MB limit)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    toast({
      title: "File too large",
      description: "Image must be less than 5MB.",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
    return;
  }
  
  // Update the thumbnail
  setIsUpdatingThumbnail(true);
  updateModelThumbnail.mutate(
    {
      modelUsername: model.username,
      thumbnailFile: file
    },
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Thumbnail updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        refetch();
      },
      onError: (error: unknown) => {
        toast({
          title: "Error",
          description: String(
            (error as any).detail
              ? typeof (error as any).detail === "object"
                ? JSON.stringify((error as any).detail)
                : (error as any).detail
              : error
          ),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
      onSettled: () => {
        setIsUpdatingThumbnail(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  );
};

  
  const handleExtend = async () => {
    if (profile?.credit! < 20) {
      toast({
        title: "Not enough credit",
        description: "You don't have enough credit to extend a model",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
  
    const isFluxModel = model.isFlux;
  
    if (!isFluxModel) {
      if (
        !model.checkpoints?.length ||
        !model.checkpoints[0]?.active ||
        expireInMilliSecs <= 0
      ) {
        return;
      }
    }
  
    try {
      setIsLoading(true);
  
      let response;
  
      if (isFluxModel) {
        response = await fetch(POST_EXTEND_FLUX(model.id), {
          method: "POST",
        });
        
        const newExpireAt = new Date().getTime() + 30 * 24 * 60 * 60 * 1000;
        setExpireInMilliSecs(newExpireAt - new Date().getTime());
      } else if (model.checkpoints?.[0]?.file_name) {
        response = await fetch(POST_EXTEND_CKPT(model.checkpoints[0].file_name), {
          method: "POST",
        });
      }
  
      refetch();
      console.log("Model extended successfully", response);
  
      toast({
        title: "Success",
        description: isFluxModel
          ? "Flux model extended successfully"
          : "Model storage extended successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      window.location.reload();
    } catch (error) {
      console.error("Error extending model:", error);
  
      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      onExtendClose();
    }
  };
  
  const isExpired = isUserModels && model.isFlux 
    ? isUserModels && fluxExpireInMilliSecs <= 0
    : (model.checkpoints?.length &&
       model.checkpoints[0].expire_at !== null &&
       (!model.checkpoints[0].active || expireInMilliSecs <= 0));

    const handleNavigation = () => {
      
      
      if (isUserModels) {
        router.push(`/creations/${model.id}`);
      } else {
        router.push(`/public-models/${model.username}`);
      }
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };
  
  const handleSaveName = () => {
    if (!modelDisplayName.trim() || modelDisplayName === model.display_name) {
      setIsEditingName(false);
      setModelDisplayName(model.display_name);
      return;
    }
    
    updateModelDisplayName.mutate(
      {
        model_id: model.id,
        display_name: modelDisplayName.trim()
      },
      {
        onSuccess: () => {
          setIsEditingName(false);
          toast({
            title: "Success",
            description: "Model name updated successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          refetch();
        },
        onError: (error:any) => {
          setIsEditingName(false);
          setModelDisplayName(model.display_name); 
          toast({
            title: "Error",
            description: String(
              (error as any).detail
                ? typeof (error as any).detail === "object"
                  ? JSON.stringify((error as any).detail)
                  : (error as any).detail
                : error
            ),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    );
  };

  const handleNameKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setModelDisplayName(model.display_name); 
    }
  };
  

  const handleToggleAutoExtend = () => {
    if (model.isFlux) {
      const newAutoExtend = !autoExtend;
      
      updateAutoExtend.mutate(
        {
          model_id: model.id,
          auto_extend: newAutoExtend,
          is_flux: true
        },
        {
          onSuccess: (data) => {
            setAutoExtend(data.auto_extend);
            toast({
              title: "Auto-extend updated",
              description: data.auto_extend 
                ? "Auto-extend enabled. We'll automatically extend your model 48 hours before expiration." 
                : "Auto-extend disabled.",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: String(
                (error as any).detail
                  ? typeof (error as any).detail === "object"
                    ? JSON.stringify((error as any).detail)
                    : (error as any).detail
                  : error
              ),
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            setAutoExtend(autoExtend);
          }
        }
      );
    } else {
      if (!model.checkpoints?.length || !model.checkpoints[0].id) {
        toast({
          title: "Error",
          description: "Cannot update auto-extend setting for this model",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      const newAutoExtend = !autoExtend;
      
      updateAutoExtend.mutate(
        {
          model_id: model.id,
          auto_extend: newAutoExtend,
          is_flux: false,
        },
        {
          onSuccess: (data) => {
            setAutoExtend(data.auto_extend);
            toast({
              title: "Auto-extend updated",
              description: data.auto_extend 
                ? "Auto-extend enabled. We'll automatically extend your model 48 hours before expiration." 
                : "Auto-extend disabled.",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: String(
                (error as any).detail
                  ? typeof (error as any).detail === "object"
                    ? JSON.stringify((error as any).detail)
                    : (error as any).detail
                  : error
              ),
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            setAutoExtend(autoExtend);
          }
        }
      );
    }
  };
  


  return (
    <Box
      borderWidth="1px"
      borderColor="#27272A"
      borderRadius="xl"
      bg="rgba(24, 24, 27, 0.5)"
      sx={{ backdropFilter: "blur(8px)" }}
      boxShadow="sm"
      overflow="hidden"
      position="relative"
      transition="transform 0.2s"
      _hover={{ transform: "translateY(-2px)" }}
      w={{ base: "142px", md: "180px", lg: "264px" }}
      display="flex"
      flexDirection="column"
      height="100%"
    >
      {/* Image section with aspect ratio */}
     
      <Box position="relative" paddingBottom="110%"> {/* 1/1.1 aspect ratio */}
        
        <Image
          src={model.thumbnail}
          alt={model.display_name || "Model"}
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          objectFit="cover"
          cursor={"pointer"}
          onClick={handleNavigation}

        />
        
        {/* Top badges */}
        <Flex 
          position="absolute"
          top="3"
          left="3"
          right="3"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          {/* Left badge - Flux or Regular */}
          {model.isFlux ? (
            <Flex
              bg="rgba(168, 85, 247, 0.2)" // purple-500/20
              color="#C084FC" // purple-400
              borderRadius="md"
              px="2"
              py="1"
              fontSize="xs"
              fontWeight="medium"
              alignItems="center"
            >
              <Text>⚡ Flux</Text>
            </Flex>
          ) : (
            <Flex
              bg="rgba(113, 113, 122, 0.2)" // zinc-500/20
              color="#A1A1AA" // zinc-400
              borderRadius="md"
              px="2"
              py="1"
              fontSize="xs"
              fontWeight="medium"
              alignItems="center"
            >
              <Text>Regular</Text>
            </Flex>
          )}
          
          {/* Right badge - Image count */}
          <Flex
            bg="rgba(0, 0, 0, 0.8)" // black/80
            color="white"
            borderRadius="full"
            px="2"
            py="1"
            fontSize="xs"
            fontWeight="medium"
          >
            <Text>{model.total_image_generations ?? 0}</Text>
          </Flex>
        </Flex>
      </Box>
      
      {/* Content section with flex-grow to push buttons to bottom */}
      <Flex 
        p="3" 
        direction="column" 
        flex="1"
        justifyContent="space-between"
      >
        {/* Top content area - model info */}
        <Box>
          {/* Model name */}
          <Text 
            mb="2" 
            fontWeight="semibold" 
            color="white"
          >
            {model.display_name}
            {isUserModels &&
              model &&
              ((!model.isFlux && (!model.checkpoints || !model.checkpoints.length)) ||
                (model.isFlux && model.statusFlux === "pending")) &&
              " (Pending)"}
          </Text>
          
          {/* Status tag */}
          {isExpired ? (
           <Box
             display="inline-block"
             bg="#27272A" 
             borderRadius="md"
             px="2"
             py="1"
             fontSize="xs"
             fontWeight="medium"
             color="#A1A1AA" 
           >
             EXPIRED
           </Box>
          ) : (
            isUserModels &&
          ((model.checkpoints?.length &&
            model.checkpoints[0].active &&
            model.checkpoints[0].expire_at !== null &&
            expireInMilliSecs > 0) ||
            (model.isFlux && fluxExpireInMilliSecs > 0)) && (
              <Flex alignItems="center" fontSize="xs" color="gray.400">
                <Text>
                  Expires in{" "}
                  {model.isFlux
                    ? Math.ceil(fluxExpireInMilliSecs / (1000 * 60 * 60 * 24))
                    : Math.ceil(expireInMilliSecs / (1000 * 60 * 60 * 24))}{" "}
                  days
                </Text>
                <Text mx="1">-</Text>
                <Text
                  as="span"
                  textDecoration="underline"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExtendOpen();
                  }}
                >
                  Extend
                </Text>
              </Flex>
            )
          )}
        </Box>
        
        {/* Bottom action buttons - always at the bottom */}
        <Flex mt="3" alignItems="center" gap="2">
          {/* Play button */}
          <Link
            href={isUserModels 
              ? `/mymodels/${model.username}/create${model.isFlux ? '-flux' : ''}`
              : `/public-models/${model.username}/create-flux`}
            onClick={(e) => {
              e.stopPropagation();
              // Prevent navigation for expired models
              if (isExpired) {
                e.preventDefault();
              }
            }}
            _hover={{ textDecoration: "none" }}
            flex="1"
            pointerEvents={isExpired ? "none" : "auto"} 
            opacity={isExpired ? 0.5 : 1} 
          >
            <Flex
              h="10"
              px="4"
              py="2"
              bg="pink.500"
              color="white"
              borderRadius="md"
              fontSize="sm"
              fontWeight="medium"
              justifyContent="center"
              alignItems="center"
              _hover={{ bg: isExpired ? "pink.500" : "pink.600" }} 
              transition="colors"
              cursor={isExpired ? "not-allowed" : "pointer"}
            >
              Play
            </Flex>
          </Link>
          
          {/* Settings button - Only show for user's own models */}
          {isUserModels && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onConfigOpen();
              }}
              cursor="pointer"
              _hover={{ textDecoration: "none" }}
            >
              <Flex
                h="9"
                w="9"
                borderWidth="1px"
                borderColor="#27272A"
                borderRadius="md"
                justifyContent="center"
                alignItems="center"
                _hover={{ bg: "#27272A" }}
                transition="colors"
              >
                <Icon 
                  viewBox="0 0 24 24" 
                  boxSize={4}
                >
                  <path 
                    d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle 
                    cx="12" 
                    cy="12" 
                    r="3" 
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Icon>
                <Box srOnly>Settings</Box>
              </Flex>
            </Box>
          )}
        </Flex>
      </Flex>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent borderRadius="0" bg="#16181F" border="1px solid white">
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to delete this model?</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              isLoading={isLoading}
            >
              Delete Model
            </Button>
            <Button onClick={onClose} ml={3}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Extend Modal */}
      <Modal isOpen={isExtendOpen} onClose={onExtendClose}>
        <ModalOverlay />
        <ModalContent borderRadius="0" bg="#16181F" border="1px solid white">
          <ModalCloseButton />
          <Flex
            justify="center"
            w="100%"
            p={10}
            px={{ base: "30px", md: "80px" }}
            minH="300px"
            direction="column"
          >
            <Text fontSize={{ base: "20px", md: "28px" }} fontWeight={500}>
              This will extend your model storage for 30 days.
            </Text>
            <Flex>
              <Button
                bg={isLoading ? "gray" : "button.500"}
                color="white"
                w="100%"
                minH="50px"
                mt="55px"
                fontSize={{ base: "12px", "2xl": "16px" }}
                _hover={isLoading ? {} : { bg: "button.500", opacity: 0.9 }}
                isDisabled={isLoading}
                onClick={handleExtend}
              >
                Extend (20 credits)
              </Button>
            </Flex>
          </Flex>
        </ModalContent>
      </Modal>
   {/* Config Modal */}
{/* Config Modal - Improved for Mobile */}
<Modal isOpen={isConfigOpen} onClose={onConfigClose} isCentered>
  <ModalOverlay backdropFilter="blur(10px)" />
  <ModalContent
    maxW={{ base: "95%", sm: "450px", md: "3xl" }}
    bgColor="#0a0a0f"
    bgImage="radial-gradient(ellipse 80% 80% at 50% -20%, rgba(120,119,198,0.3), rgba(255,255,255,0))"
    borderRadius="lg"
    borderColor="#27272A"  
    borderWidth="1px"
    boxShadow="lg"
  >
    <Flex direction="column" py={{ base: 4, md: 8 }} px={{ base: 3, md: 4 }}>
      {/* Back Button */}
      <Flex align="center" mb={4}>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          fontSize="sm"
          color="gray.400"
          _hover={{ color: "white" }}
          onClick={onConfigClose}
        >
          <Icon viewBox="0 0 24 24" mr={1} boxSize={4}>
            <path 
              d="m15 18-6-6 6-6" 
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Icon>
          Go Back
        </Box>
      </Flex>
      
      {/* Main Content Container */}
      <Box
        borderRadius="lg"
        borderColor="#27272A"  
        borderWidth="1px"
        bg="zinc.900"
        backdropFilter="blur-xl"
        shadow="sm"
      >
        {/* Model Title Section */}
        <Flex direction="column" p={{ base: 4, md: 6 }} pb={{ base: 2, md: 4 }}>
          <Flex 
            justify="space-between" 
            align="center"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <Flex 
              align="center" 
              w={{ base: "100%", md: "auto" }}
              mb={{ base: 2, md: 0 }}
            >
              {isEditingName ? (
                <InputGroup size="md" maxW={{ base: "full", md: "300px" }}>
                  <Input
                    ref={nameInputRef}
                    value={modelDisplayName}
                    onKeyDown={handleNameKeyDown}
                    onChange={(e) => setModelDisplayName(e.target.value)}
                    onBlur={handleSaveName}
                    placeholder="Model name"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="semibold"
                    borderColor="#27272A"
                    _hover={{ borderColor: "gray.600" }}
                    _focus={{ borderColor: "purple.500", boxShadow: "none" }}
                    maxLength={50}
                  />
                  <InputRightElement>
                    <Button 
                      size="sm" 
                      onClick={handleSaveName} 
                      variant="ghost" 
                      mr={1}
                    >
                      Save
                    </Button>
                  </InputRightElement>
                </InputGroup>
              ) : (
                <>
                  <Text 
                    fontSize={{ base: "lg", md: "xl" }} 
                    fontWeight="semibold"
                    isTruncated
                    maxW={{ base: "70vw", sm: "300px", md: "full" }}
                  >
                    {modelDisplayName}
                  </Text>
                  <Box
                    as="button"
                    rounded="md"
                    fontSize="sm"
                    fontWeight="medium"
                    transition="colors"
                    _hover={{ bg: "gray.700", color: "white" }}
                    h={10}
                    w={10}
                    ml={2}
                    onClick={handleEditName}
                  >
                    <Icon viewBox="0 0 24 24" boxSize={4}>
                      <path 
                        d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" 
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path 
                        d="m15 5 4 4" 
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Icon>
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
        
        <Flex 
          direction="column" 
          p={{ base: 4, md: 6 }} 
          pt={{ base: 0, md: 0 }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          <Flex 
            direction={{ base: "column", md: "row" }}
            align={{ base: "center", md: "start" }}
            gap={4}
          >
            <Box
              position="relative"
              w={{ base: "200px", md: "160px" }}
              h={{ base: "200px", md: "160px" }}
              minW={{ base: "200px", md: "160px" }}
              overflow="hidden"
              rounded="lg"
              borderWidth="1px"
              borderColor="#27272A"
              bgColor="rgba(39, 39, 42, 0.5)"
              display="flex"
              role="group"
              mx={{ base: "auto", md: 0 }}
            >
              <Flex
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bg="rgba(0, 0, 0, 0.5)"
                alignItems="center"
                justifyContent="center"
                opacity="0"
                _groupHover={{ opacity: 1 }}
                _active={{ opacity: 1 }} 
                transition="opacity 0.2s"
                cursor="pointer"
                onClick={handleFileSelect}
              >
                <Flex
                  direction="column"
                  alignItems="center"
                  color="white"
                  p={2}
                >
                  <Icon 
                    viewBox="0 0 24 24" 
                    boxSize={6}
                    mb={1}
                  >
                    <path 
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" 
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline 
                      points="17 8 12 3 7 8" 
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <line 
                      x1="12" 
                      x2="12" 
                      y1="3" 
                      y2="15" 
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Icon>
                  <Text fontSize="xs" fontWeight="medium" textAlign="center">
                    {isUpdatingThumbnail ? "Uploading..." : "Change Image"}
                  </Text>
                </Flex>
              </Flex>
              
              <Image
                alt={model.display_name}
                src={model.thumbnail}
                objectFit="contain"
                height="100%"
                width="100%"
              />
            </Box>
            
            <Flex 
              direction="column" 
              flex="1"
              w={{ base: "full", md: "auto" }}
              align={{ base: "center", md: "flex-start" }}
              mt={{ base: 3, md: 0 }}
            >
              <Box 
                mb={3}
                textAlign={{ base: "center", md: "left" }}
                w={{ base: "full", md: "auto" }}
              >
                <Text as="label" fontSize="sm" fontWeight="medium">
                  Status
                </Text>
                <Text fontSize="sm" color="gray.400">
                  {isExpired ? (
                    "This model has expired"
                  ) : (
                    `Expires in ${
                      model.isFlux
                        ? Math.ceil(fluxExpireInMilliSecs / (1000 * 60 * 60 * 24))
                        : Math.ceil(expireInMilliSecs / (1000 * 60 * 60 * 24))
                    } days`
                  )}
                </Text>
              </Box>
              
              {/* Auto-extend Toggle - Only show if model is NOT expired */}
              {!isExpired && (
                <>
                  <Flex 
                    align="center" 
                    mb={2}
                    w={{ base: "full", md: "auto" }}
                    justify={{ base: "center", md: "flex-start" }}
                  >
                    <Switch
                      id="auto-extend"
                      isChecked={autoExtend}
                      onChange={handleToggleAutoExtend}
                      cursor="pointer"
                      borderWidth="2px"
                      borderColor="transparent"
                      transition="colors"
                      data-state={autoExtend ? "checked" : "unchecked"}
                      colorScheme="purple"
                      mr={2}
                    />
                    <FormLabel
                      htmlFor="auto-extend"
                      fontWeight="medium"
                      fontSize="sm"
                      mb={0}
                      cursor="pointer"
                    >
                      Auto-extend Model
                    </FormLabel>
                  </Flex>
                  
                  <Text 
                    fontSize="xs" 
                    color="gray.400"
                    textAlign={{ base: "center", md: "left" }}
                    w={{ base: "full", md: "auto" }}
                    px={{ base: 2, md: 0 }}
                  >
                    When enabled, we&apos;ll automatically extend your model 48 hours before expiration.
                  </Text>
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
        
        <Flex
          align={{ base: "stretch", md: "center" }}
          p={{ base: 4, md: 6 }}
          direction={{ base: "column", sm: "row" }}
          justify={{ base: "center", sm: "space-between" }}
          gap={{ base: 3, sm: 0 }}
          borderTopWidth="1px"
          borderTopColor="#27272A"
          pt={4}
        >
          <Button
            display="inline-flex"
            rounded="md"
            fontSize="sm"
            fontWeight="medium"
            transition="colors"
            borderWidth="1px"
            borderColor="gray.600"
            bg="transparent"
            _hover={{ bg: "gray.700", color: "white" }}
            h={10}
            px={4}
            py={2}
            w={{ base: "full", sm: "auto" }}
            leftIcon={<Icon color="white" as={FaTrash} boxSize={4} />}
            onClick={onOpen}
          >
            <Text color="white" fontWeight={600}>
              Delete Model
            </Text>
          </Button>
          
          {/* Only show Extend button if model is NOT expired */}
          {!isExpired && (
            <Button
              display="inline-flex"
              rounded="md"
              fontSize="sm"
              fontWeight="medium"
              transition="colors"
              h={10}
              px={4}
              py={2}
              w={{ base: "full", sm: "auto" }}
              bgGradient="linear(to-r, pink.500, purple.500)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, pink.600, purple.600)" }}
              onClick={onExtendOpen}
              order={{ base: -1, sm: 1 }} 
            >
              Extend Now (20 credits)
            </Button>
          )}
        </Flex>
      </Box>
    </Flex>
  </ModalContent>
</Modal>
    </Box>
  );
};

export default ModelCard;